import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import styles from "./crmDashboard.module.css";
import ex from "../Crmexplorer.module.css"
import { useCrmBackend } from "../Crmbackend";
import { Skeleton } from "../Crmexplorer";

/**
 * Home dashboard. Signed in (a token in localStorage): every figure comes from GET api/insights/dashboard?period=&workspace= and the page
 * refreshes itself when something changes in the CRM. Not signed in: a sign-in note (no sample figures).
 * Not run against the live service: the field names below are read from the C# source (Crm.Insights/Dashboard.cs).
 */

function Icon({
  name,
  size = 18,
}: {
  name: "grid" | "search" | "bell" | "plus" | "arrow" | "chevron" | "sparkle";
  size?: number;
}) {
  const paths: Record<string, ReactNode> = {
    grid: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="2" />
        <rect x="14" y="3" width="7" height="7" rx="2" />
        <rect x="3" y="14" width="7" height="7" rx="2" />
        <rect x="14" y="14" width="7" height="7" rx="2" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    bell: (
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
      </>
    ),
    plus: (
      <>
        <path d="M12 5v14M5 12h14" />
      </>
    ),
    arrow: (
      <>
        <path d="m7 17 10-10M8 7h9v9" />
      </>
    ),
    chevron: <path d="m9 18 6-6-6-6" />,
    sparkle: (
      <>
        <path d="m12 3 1.2 4.1a5 5 0 0 0 3.4 3.4l4.1 1.2-4.1 1.2a5 5 0 0 0-3.4 3.4L12 20.4l-1.2-4.1a5 5 0 0 0-3.4-3.4l-4.1-1.2 4.1-1.2a5 5 0 0 0 3.4-3.4L12 3Z" />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function Button({
  children,
  className = "",
  onClick,
  label,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  label?: string;
}) {
  return (
    <button type="button" className={className} onClick={onClick} aria-label={label}>
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ sample figures (shown when not signed in) */

const goals = [
  { label: "Enterprise sales", value: "$684k", progress: 82 },
  { label: "New customers", value: "186", progress: 68 },
  { label: "Renewals", value: "$420k", progress: 91 },
  { label: "Upsell revenue", value: "$148k", progress: 54 },
];

const pipeline = [
  { name: "Discovery", value: "$320k", className: "violet" },
  { name: "Proposal", value: "$276k", className: "blue" },
  { name: "Negotiation", value: "$189k", className: "cyan" },
  { name: "Contract", value: "$96k", className: "yellow" },
];

const activities = [
  { initials: "KM", text: "Kira moved Atlas Labs to Contract", time: "8 min", color: "lilac" },
  { initials: "JL", text: "James created invoice #2048", time: "32 min", color: "mint" },
  { initials: "SO", text: "Sofia added 250 units to inventory", time: "1 hr", color: "peach" },
];

/* ------------------------------------------------------------------ what the service sends */

interface ApiMetric { key: string; label: string; value: number | null; currency: string | null; deltaPct: number | null; status: string; note?: string | null; progress?: number | null }
interface ApiDashboard {
  currency: string | null;
  metrics: { revenue?: ApiMetric | null; netCashFlow?: ApiMetric | null; operatingCosts?: ApiMetric | null; openPipeline?: ApiMetric | null; quotaAttainment?: ApiMetric | null };
  goals?: { key: string; label: string; actual: number; target: number; currency: string | null; unit: string; progress: number }[] | null;
  planProgress?: number | null;
  pipeline?: { total: number | null; stages: { name: string; label: string; value: number; count: number; color: string }[] } | null;
  health?: { overall: number | null; axes: { key: string; label: string; score: number | null; status: string; basis: string }[] } | null;
  leadSources?: { total: number; items: { source: string; count: number }[]; groups: { name: string; count: number; pct: number }[] } | null;
  workingCapital?: { key: string; label: string; value: number | null; status: string }[] | null;
  revenueTrend?: { available: boolean; months: { month: string; invoicedBase: number | null }[]; previousYear: { month: string; invoicedBase: number | null }[] } | null;
  ordersVsTarget?: { monthlyTargetBase: number | null; months: { month: string; count: number; totalBase: number; targetBase: number | null; pct: number | null }[] } | null;
  teamFeed?: { initials: string | null; text: string | null; time: string | null; color: string | null }[] | null;
  pending?: string[];
  warnings?: string[] | null;
}

const SWATCH: Record<string, string> = { violet: "#745fe7", blue: "#3b82f6", cyan: "#06b6d4", yellow: "#f5b82e", green: "#22c55e", orange: "#f97316" };
const SOURCE_COLORS = ["green", "violet", "blue", "yellow", "orange"];
// radar: outer corner of each axis (same pentagon as the grid) and the centre
const RADAR: Record<string, [number, number]> = { sales: [110, 17], cash: [175, 55], service: [157, 130], supply: [63, 130], people: [45, 55] };
const RADAR_CENTRE: [number, number] = [110, 80];

const money = (v: number | null | undefined, currency: string | null | undefined): string => {
  if (v === null || v === undefined) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: currency ? "currency" : "decimal", currency: currency || undefined, notation: "compact", maximumFractionDigits: 1 }).format(v);
  } catch {
    return String(Math.round(v));
  }
};
const delta = (m?: ApiMetric | null): string => {
  if (!m) return "";
  if (m.status !== "ok") return m.note || (m.status === "unavailable" ? "Unavailable right now" : "Waiting for data");
  if (m.deltaPct === null || m.deltaPct === undefined) return m.note || "No comparison yet";
  return `${m.deltaPct >= 0 ? "+" : ""}${m.deltaPct.toFixed(1)}% vs last period`;
};
const ago = (iso: string | null): string => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (Number.isNaN(s)) return "";
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min`;
  if (s < 86400) return `${Math.round(s / 3600)} hr`;
  return `${Math.round(s / 86400)} d`;
};
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function MetricCard({
  label,
  value,
  delta,
  tone = "neutral",
  progress,
}: {
  label: string;
  value: string;
  delta: string;
  tone?: string;
  progress?: number;
}) {
  return (
    <article className={`${styles.card} ${styles["metric-card"]}`}>
      <div className={styles["card-label"]}>{label}</div>
      <div className={`${styles["metric-number"]} ${styles[tone] || ""}`}>{value}</div>
      {progress !== undefined && (
        <div className={styles["micro-progress"]}>
          <span style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className={`${styles["metric-delta"]} ${styles[tone] || ""}`}>{delta}</div>
    </article>
  );
}

function Donut({
  center,
  variant = "pipeline",
  parts,
}: {
  center: string;
  variant?: "pipeline" | "source";
  /** live figures: slices of the ring. Left out, the ring keeps the colours from the stylesheet. */
  parts?: { value: number; color: string }[];
}) {
  let background: string | undefined;
  if (parts) {
    const total = parts.reduce((t, p) => t + Math.max(0, p.value), 0);
    if (total <= 0) background = "#ececf1";
    else {
      let at = 0;
      background = `conic-gradient(${parts.map((p) => { const from = at; at += (Math.max(0, p.value) / total) * 100; return `${p.color} ${from}% ${at}%`; }).join(", ")})`;
    }
  }
  return (
    <div className={`${styles.donut} ${styles[`donut-${variant}`]}`} style={background ? { background } : undefined}>
      <div>
        <strong>{center}</strong>
        <span>{variant === "pipeline" ? "Pipeline" : "Leads"}</span>
      </div>
    </div>
  );
}

const CrmDashboard: React.FC = () => {
  const be = useCrmBackend();
  const signedIn = (() => { try { return !!localStorage.getItem("authToken"); } catch { return false; } })();
  const wantLive = !!be && signedIn;
  const api = be?.api;
  const subscribe = be?.subscribe;
  const resyncTick = be?.resyncTick ?? 0;

  const [period, setPeriod] = useState<"Quarter" | "Year">("Quarter");
  const [workspace, setWorkspace] = useState<"Overview" | "CRM" | "ERP">("Overview");
  const [notice, setNotice] = useState("");
  const [leads, setLeads] = useState(428);
  const [data, setData] = useState<ApiDashboard | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const seq = useRef(0);

  function notify(message: string) {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 2400);
  }

  function addLead() {
    setLeads((current) => current + 1);
    notify("New lead created");
  }
  void addLead;

  const load = useCallback(async () => {
    if (!api) return;
    const my = ++seq.current;
    try {
      const d = await api.json<ApiDashboard>("GET", "api/insights/dashboard", { query: { period, workspace } });
      if (my !== seq.current) return;
      setData(d); setStatus("ready"); setError("");
    } catch (e) {
      if (my !== seq.current) return;
      const st = typeof (e as { status?: unknown })?.status === "number" ? (e as { status: number }).status : 0;
      setError(st === 403 ? "You don't have permission to see the dashboard." : st === 401 ? "The CRM service did not accept your sign-in. Sign in again." : e instanceof Error ? e.message : "Could not load the dashboard.");
      setStatus((s) => (s === "ready" ? s : "error"));
    }
  }, [api, period, workspace]);

  useEffect(() => {
    if (!wantLive) return;
    setStatus((s) => (s === "ready" ? s : "loading"));
    void load();
  }, [wantLive, load, resyncTick, retry]);

  // any change in the CRM -> refresh the figures (at most once every 2 seconds)
  useEffect(() => {
    if (!wantLive || !subscribe) return;
    let timer: number | undefined;
    const off = subscribe(() => { window.clearTimeout(timer); timer = window.setTimeout(() => void load(), 2000); });
    return () => { off(); window.clearTimeout(timer); };
  }, [wantLive, subscribe, load]);

  const cur = data?.currency ?? null;
  const live = wantLive && status === "ready" && !!data;

  /* ---- figures for the cards: live or sample ---- */
  const m = data?.metrics;
  const bars = useMemo(() => {
    if (live && data?.ordersVsTarget && data.ordersVsTarget.months.length > 0) {
      const months = data.ordersVsTarget.months;
      const top = Math.max(1, ...months.map((x) => Math.max(x.totalBase, x.targetBase ?? 0)));
      return months.map((x) => ({ h: Math.max(4, Math.round((x.totalBase / top) * 100)), hit: (x.pct ?? 0) >= 100, label: x.month }));
    }
    const sample = period === "Quarter" ? [36, 48, 66, 74, 85, 61, 53, 78, 57, 63, 46, 54] : [46, 56, 49, 72, 64, 82, 69, 88, 76, 91, 80, 94];
    return sample.map((h, i) => ({ h, hit: i === 3 || i === 7, label: "" }));
  }, [live, data, period]);

  const trend = useMemo(() => {
    const months = data?.revenueTrend?.months;
    if (!live || !months || months.length === 0) return null;
    const vals = months.map((x) => x.invoicedBase);
    const top = Math.max(1, ...vals.map((v) => v ?? 0), ...(data?.revenueTrend?.previousYear ?? []).map((x) => x.invoicedBase ?? 0));
    const pts: [number, number][] = [];
    vals.forEach((v, i) => { if (v !== null) pts.push([(i / (vals.length - 1)) * 450, 120 - (v / top) * 100]); });
    if (pts.length === 0) return null;
    const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
    const last = pts[pts.length - 1]!;
    return { line, area: `${line} L${last[0].toFixed(1)} 130 L${pts[0]![0].toFixed(1)} 130Z` };
  }, [live, data]);

  const radar = useMemo(() => {
    const axes = data?.health?.axes;
    if (!live || !axes) return null;
    const pts = axes.map((a) => {
      const [ox, oy] = RADAR[a.key] ?? RADAR_CENTRE;
      const k = (a.score ?? 0) / 100;
      return [RADAR_CENTRE[0] + (ox - RADAR_CENTRE[0]) * k, RADAR_CENTRE[1] + (oy - RADAR_CENTRE[1]) * k] as [number, number];
    });
    return { pts, d: `${pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ")}Z` };
  }, [live, data]);

  const pipelineParts = live ? (data?.pipeline?.stages ?? []).map((s) => ({ value: s.value, color: SWATCH[s.color] ?? "#9aa0b4" })) : undefined;
  const sourceGroups = live ? (data?.leadSources?.groups ?? []) : [];
  const sourceParts = live ? sourceGroups.map((g, i) => ({ value: g.count, color: SWATCH[SOURCE_COLORS[i % SOURCE_COLORS.length] ?? "violet"] ?? "#745fe7" })) : undefined;
  const leadTotal = live ? data?.leadSources?.total ?? 0 : leads;

  const headline = (): ReactNode => {
    if (live && data) {
      if (workspace === "CRM") {
        const d = m?.openPipeline?.deltaPct;
        return d === null || d === undefined ? <>Your customer pipeline at a glance.</> : <>Your customer pipeline is <strong>{Math.abs(d).toFixed(1)}% {d >= 0 ? "stronger" : "weaker"}</strong> than at the start of the period.</>;
      }
      if (workspace === "ERP") {
        const h = data.health?.overall;
        return h === null || h === undefined ? <>Your operations at a glance.</> : <>Your business health score is <strong>{h}%</strong> this period.</>;
      }
      const d = m?.revenue?.deltaPct;
      return d === null || d === undefined ? <>Your revenue at a glance.</> : <>Your revenue is <strong>{Math.abs(d).toFixed(1)}% {d >= 0 ? "ahead of" : "behind"}</strong> the previous period.</>;
    }
    if (workspace === "CRM") return <>Your customer pipeline is <strong>18.2% stronger</strong> than last quarter.</>;
    if (workspace === "ERP") return <>Your operations are running at <strong>94% efficiency</strong> this month.</>;
    return <>Your revenue engine is <strong>12.4% ahead</strong> of this quarter’s plan.</>;
  };

  // no sample figures: signed out, the dashboard shows nothing but this note
  if (!wantLive) {
    return (
      <div className={styles.scene}>
        <main className={styles["erp-board"]}>
          <div className={ex.cxBanner} role="status">Sign in to see the dashboard. Every figure on it comes from the CRM service.</div>
        </main>
      </div>
    );
  }

  if (wantLive && status !== "ready") {
    return (
      <div className={styles.scene}>
        <main className={styles["erp-board"]}>
          {status === "error" ? (
            <div className={`${ex.cxBanner} ${ex.cxBannerErr}`}>
              Could not load the dashboard. {error}
              <button type="button" className={ex.cxBtn} onClick={() => setRetry((n) => n + 1)}>Retry</button>
            </div>
          ) : (
            <Skeleton tiles rows={8} cols={5} />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className={styles.scene}>
      <main className={styles["erp-board"]}>
        <section className={styles["intro-row"]}>
          <div className={styles["controls-row"]}>
            <div className={styles["header-center"]}>
              <span
                className={`${styles["tab-indicator"]} ${styles[`tab-${workspace.toLowerCase()}`] || ""
                  }`}
              />
              <Button
                className={`${styles["nav-pill"]} ${workspace === "Overview" ? styles.active : ""
                  }`}
                onClick={() => setWorkspace("Overview")}
              >
                Overview
              </Button>
              <Button
                className={`${styles["nav-pill"]} ${workspace === "CRM" ? styles.active : ""
                  }`}
                onClick={() => setWorkspace("CRM")}
              >
                CRM
              </Button>
              <Button
                className={`${styles["nav-pill"]} ${workspace === "ERP" ? styles.active : ""
                  }`}
                onClick={() => setWorkspace("ERP")}
              >
                ERP
              </Button>
            </div>

            <div className={styles["period-switch"]} aria-label="Report period">
              {(["Quarter", "Year"] as const).map((item) => (
                <Button
                  key={item}
                  className={period === item ? styles.selected : ""}
                  onClick={() => setPeriod(item)}
                >
                  {item}
                </Button>
              ))}
            </div>
          </div>
          <div>

            <p className={styles["dtitle"]} role="heading" aria-level={1}>
              {headline()}
            </p>
          </div>
        </section>

        {live && (data?.warnings?.length ?? 0) > 0 && (
          <div className={ex.cxBanner}>{data?.warnings?.join(" · ")}</div>
        )}

        <section className={styles["top-metrics"]}>
          {live ? (
            <>
              <MetricCard label="Total revenue" value={money(m?.revenue?.value, m?.revenue?.currency ?? cur)} delta={delta(m?.revenue)} tone="purple" progress={m?.revenue?.progress ?? undefined} />
              <MetricCard label="Net cash flow" value={money(m?.netCashFlow?.value, m?.netCashFlow?.currency ?? cur)} delta={delta(m?.netCashFlow)} tone="green" />
              <MetricCard label="Operating costs" value={money(m?.operatingCosts?.value, m?.operatingCosts?.currency ?? cur)} delta={delta(m?.operatingCosts)} tone="red" />
              <MetricCard label="Open pipeline" value={money(m?.openPipeline?.value, m?.openPipeline?.currency ?? cur)} delta={delta(m?.openPipeline)} tone="blue" />
              <MetricCard
                label="Quota attainment"
                value={m?.quotaAttainment?.value === null || m?.quotaAttainment?.value === undefined ? "—" : `${Math.round(m.quotaAttainment.value)}%`}
                delta={m?.quotaAttainment?.note || delta(m?.quotaAttainment)}
                tone="purple"
                progress={m?.quotaAttainment?.progress ?? undefined}
              />
            </>
          ) : (
            <>
              <MetricCard label="Total revenue" value={period === "Quarter" ? "$842,900" : "$3.2M"} delta="+12.4% vs last period" tone="purple" progress={72} />
              <MetricCard label="Net cash flow" value="$178,400" delta="+8.9% vs last period" tone="green" />
              <MetricCard label="Operating costs" value="$286,500" delta="+3.1% vs last period" tone="red" />
              <MetricCard label="Open pipeline" value="$881,200" delta="+18.2% vs last period" tone="blue" />
              <MetricCard label="Quota attainment" value="76%" delta="On track" tone="purple" progress={76} />
            </>
          )}
        </section>

        <section className={styles["data-grid"]}>
          <article className={`${styles.card} ${styles["goals-card"]}`}>
            <div className={styles["card-label"]}>Quarterly goals</div>
            <div className={styles["goal-list"]}>
              {(live
                ? (data?.goals ?? []).map((g) => ({ label: g.label, value: g.currency ? money(g.actual, g.currency) : g.unit === "percent" ? `${Math.round(g.actual)}%` : String(Math.round(g.actual)), progress: g.progress }))
                : goals
              ).map((goal) => (
                <div className={styles["goal-row"]} key={goal.label}>
                  <div>
                    <span>{goal.label}</span>
                    <strong>{goal.value}</strong>
                  </div>
                  <div className={styles["goal-track"]}>
                    <i style={{ width: `${Math.min(100, goal.progress)}%` }} />
                  </div>
                </div>
              ))}
              {live && (data?.goals ?? []).length === 0 && <div className={ex.cxMuted}>No goals are set for this period.</div>}
            </div>
            <div className={styles["goal-score"]}>
              <strong>{live ? (data?.planProgress === null || data?.planProgress === undefined ? "—" : `${data.planProgress}%`) : "76%"}</strong>
              <span>of total plan</span>
            </div>
          </article>

          <article className={`${styles.card} ${styles["pipeline-card"]}`}>
            <div className={styles["card-top"]}>
              <div>
                <div className={styles["card-label"]}>Sales pipeline</div>
                <p>Deals by current stage</p>
              </div>
              <Button
                className={styles["tiny-link"]}
                onClick={() => notify("Pipeline details opened")}
              >
                Details <Icon name="arrow" size={13} />
              </Button>
            </div>
            <div className={styles["visual-split"]}>
              <Donut center={live ? money(data?.pipeline?.total, cur) : "$881k"} parts={pipelineParts} />
              <div className={styles["legend-list"]}>
                {live
                  ? (data?.pipeline?.stages ?? []).map((s) => (
                    <div key={s.name}>
                      <i className={styles[s.color]} style={styles[s.color] ? undefined : { background: SWATCH[s.color] }} />
                      <span>{s.label}</span>
                      <strong>{money(s.value, cur)}</strong>
                    </div>
                  ))
                  : pipeline.map((item) => (
                    <div key={item.name}>
                      <i className={styles[item.className]} />
                      <span>{item.name}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                {live && (data?.pipeline?.stages ?? []).length === 0 && <div className={ex.cxMuted}>No open deals.</div>}
              </div>
            </div>
          </article>

          <article className={`${styles.card} ${styles["radar-card"]}`}>
            <div className={styles["card-label"]}>Business health</div>
            <p>Performance across core functions</p>
            <div className={styles["radar-wrap"]}>
              <svg
                viewBox="0 0 220 164"
                role="img"
                aria-label="Business health radar chart"
              >
                <g className={styles["radar-grid"]}>
                  <path d="M110 17 175 55 157 130 63 130 45 55Z" />
                  <path d="M110 37 155 63 143 115 77 115 65 63Z" />
                  <path d="M110 57 136 71 128 99 92 99 84 71Z" />
                  <path d="M110 17V130M45 55l112 75M175 55 63 130M175 55 63 130" />
                </g>
                <path
                  className={styles["radar-shape"]}
                  d={radar ? radar.d : "M110 30 159 64 142 117 72 124 61 61Z"}
                />
                {(radar ? radar.pts : [
                  [110, 30],
                  [159, 64],
                  [142, 117],
                  [72, 124],
                  [61, 61],
                ]).map(([x, y]) => (
                  <circle key={`${x}-${y}`} cx={x} cy={y} r="3" />
                ))}
                <g className={styles["radar-labels"]}>
                  <text x="110" y="10">
                    Sales
                  </text>
                  <text x="188" y="57">
                    Cash
                  </text>
                  <text x="163" y="148">
                    Service
                  </text>
                  <text x="36" y="148">
                    Supply
                  </text>
                  <text x="8" y="57">
                    People
                  </text>
                </g>
              </svg>
            </div>
            {live && (data?.health?.axes ?? []).some((a) => a.score === null) && (
              <p className={ex.cxMuted}>{(data?.health?.axes ?? []).filter((a) => a.score === null).map((a) => a.label).join(", ")}: no figure yet.</p>
            )}
          </article>

          <article className={`${styles.card} ${styles["source-card"]}`}>
            <div className={styles["card-top"]}>
              <div>
                <div className={styles["card-label"]}>Lead sources</div>
                <p>Acquisition mix this quarter</p>
              </div>
              <strong className={styles["lead-total"]}>{leadTotal}</strong>
            </div>
            <div className={styles["visual-split"]}>
              <Donut center={String(leadTotal)} variant="source" parts={sourceParts} />
              <div className={styles["legend-list"]}>
                {live ? (
                  sourceGroups.length === 0 ? <div className={ex.cxMuted}>No leads in this period.</div> : sourceGroups.map((g, i) => {
                    const c = SOURCE_COLORS[i % SOURCE_COLORS.length] ?? "violet";
                    return (
                      <div key={g.name}>
                        <i className={styles[c]} style={styles[c] ? undefined : { background: SWATCH[c] }} />
                        <span>{g.name}</span>
                        <strong>{g.pct}%</strong>
                      </div>
                    );
                  })
                ) : (
                  <>
                    <div>
                      <i className={styles.green} />
                      <span>Organic</span>
                      <strong>38%</strong>
                    </div>
                    <div>
                      <i className={styles.violet} />
                      <span>Referral</span>
                      <strong>27%</strong>
                    </div>
                    <div>
                      <i className={styles.blue} />
                      <span>Outbound</span>
                      <strong>21%</strong>
                    </div>
                    <div>
                      <i className={styles.yellow} />
                      <span>Paid</span>
                      <strong>14%</strong>
                    </div>
                  </>
                )}
              </div>
            </div>
          </article>

          <article className={`${styles.card} ${styles["balances-card"]}`}>
            <div className={styles["card-label"]}>Working capital</div>
            <div className={styles["balance-list"]}>
              {live ? (
                (["cash", "receivables", "inventory", "payables"] as const).map((k) => {
                  const row = data?.workingCapital?.find((x) => x.key === k);
                  const cls = k === "receivables" ? styles.purple : k === "inventory" ? styles.blue : k === "payables" ? styles.red : undefined;
                  return (
                    <div key={k}>
                      <span>{row?.label ?? k[0]!.toUpperCase() + k.slice(1)}</span>
                      <strong className={cls}>{row && row.value !== null ? money(row.value, cur) : "Pending"}</strong>
                    </div>
                  );
                })
              ) : (
                <>
                  <div>
                    <span>Cash</span>
                    <strong>$751k</strong>
                  </div>
                  <div>
                    <span>Receivables</span>
                    <strong className={styles.purple}>$225k</strong>
                  </div>
                  <div>
                    <span>Inventory</span>
                    <strong className={styles.blue}>$184k</strong>
                  </div>
                  <div>
                    <span>Payables</span>
                    <strong className={styles.red}>$163k</strong>
                  </div>
                </>
              )}
            </div>
            <Button
              className={`${styles["tiny-link"]} ${styles["bottom-link"]}`}
              onClick={() => notify("Finance module opened")}
            >
              Finance module <Icon name="chevron" size={12} />
            </Button>
          </article>

          <article className={`${styles.card} ${styles["trend-card"]}`}>
            <div className={styles["card-top"]}>
              <div>
                <div className={styles["card-label"]}>Revenue dynamics</div>
                <p>Actual revenue over the selected period</p>
              </div>
              <strong>{live ? money(m?.revenue?.value, m?.revenue?.currency ?? cur) : period === "Quarter" ? "$842.9k" : "$3.2M"}</strong>
            </div>
            <svg
              className={styles["trend-chart"]}
              viewBox="0 0 450 130"
              preserveAspectRatio="none"
              role="img"
              aria-label="Revenue trend line chart"
            >
              <defs>
                <linearGradient id="lineArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#745fe7" stopOpacity=".22" />
                  <stop offset="100%" stopColor="#745fe7" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path className={styles["chart-grid"]} d="M0 30H450M0 65H450M0 100H450" />
              <path
                className={styles.area}
                d={trend ? trend.area : "M0 106 C42 87 56 82 92 72 S141 41 178 58 S220 75 257 48 S304 68 339 55 S395 25 450 42 L450 130H0Z"}
              />
              <path
                className={styles["trend-line"]}
                d={trend ? trend.line : "M0 106 C42 87 56 82 92 72 S141 41 178 58 S220 75 257 48 S304 68 339 55 S395 25 450 42"}
              />
            </svg>
            <div className={styles.axis}>
              <span>JAN</span>
              <span>MAR</span>
              <span>MAY</span>
              <span>JUL</span>
              <span>SEP</span>
              <span>DEC</span>
            </div>
          </article>

          <article className={`${styles.card} ${styles["activity-card"]}`}>
            <div className={styles["card-top"]}>
              <div>
                <div className={styles["card-label"]}>Team activity</div>
                <p>Latest operational updates</p>
              </div>
              <Icon name="sparkle" size={17} />
            </div>
            <div className={styles["activity-list"]}>
              {(live
                ? (data?.teamFeed ?? []).map((f) => ({ initials: f.initials ?? "", text: f.text ?? "", time: ago(f.time), color: f.color ?? "lilac" }))
                : activities
              ).map((item, i) => (
                <div className={styles["activity-item"]} key={`${item.text}-${i}`}>
                  <div className={`${styles["mini-avatar"]} ${styles[item.color]}`}>
                    {item.initials}
                  </div>
                  <span>{item.text}</span>
                  <time>{item.time}</time>
                </div>
              ))}
              {live && (data?.teamFeed ?? []).length === 0 && <div className={ex.cxMuted}>No recent activity.</div>}
            </div>
          </article>

          <article className={`${styles.card} ${styles["bars-card"]}`}>
            <div className={styles["card-top"]}>
              <div>
                <div className={styles["card-label"]}>Orders vs target</div>
                <p>Monthly order volume</p>
              </div>
              <strong>{live ? (data?.ordersVsTarget?.months ?? []).reduce((t, x) => t + x.count, 0).toLocaleString() : "1,284"}</strong>
            </div>
            <div className={styles.bars}>
              {bars.map((b, index) => (
                <i
                  key={index}
                  style={{ height: `${b.h}%` }}
                  className={b.hit ? styles.highlight : ""}
                  title={b.label || undefined}
                />
              ))}
            </div>
            <div className={styles.axis}>
              {live && data?.ordersVsTarget && data.ordersVsTarget.months.length > 0 ? (
                [0, 3, 6, 9, 11].map((i) => <span key={i}>{MONTHS[Number((data.ordersVsTarget?.months[i]?.month ?? "01-01").slice(5, 7)) - 1] ?? ""}</span>)
              ) : (
                <>
                  <span>JAN</span>
                  <span>APR</span>
                  <span>JUL</span>
                  <span>OCT</span>
                  <span>DEC</span>
                </>
              )}
            </div>
          </article>
        </section>
      </main>

      {notice && (
        <div className={styles.toast} role="status">
          {notice}
        </div>
      )}
    </div>
  );
};

export default CrmDashboard;